import { LoadingState } from "@repo/app-types/client/client";
import type { Integration } from "@repo/app-types/entities/integration";
import { buildProjectUrlPath } from "@repo/app-types/helpers/adminAppRoutes";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import { createRecruitingProject } from "../projectsSlice.js";

export interface CreateProjectViaFountainModalProps {
  isOpen: boolean;
  onClose: () => void;
  fountainIntegration: Integration;
}

export const CreateProjectViaFountainModal: FC<
  CreateProjectViaFountainModalProps
> = ({ isOpen, onClose, fountainIntegration }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [fountainPositions, setFountainPositions] = useState<
    readonly SimpleSelectOption<string>[]
  >([]);
  const [selectedFountainPositionId, setSelectedFountainPositionId] = useState<
    string | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fountainPositionLoadingState, setFountainPositionLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);
  const [createProjectStatus, setCreateProjectStatus] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const teammate = useAppSelector(selectTeammate);
  if (!teammate) {
    throw new Error("Teammate not found");
  }

  const isLoadingPositions =
    fountainPositionLoadingState === LoadingState.LOADING;

  const fetchFountainPositions = async () => {
    setFountainPositionLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.post(
        `/integrations/${fountainIntegration.id}/job-types`,
      );

      const selectOptions = (data as { name: string; id: string }[])
        .map((position) => ({
          label: position.name,
          value: position.id,
        }))
        .sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
        );

      setFountainPositions(selectOptions);
      setFountainPositionLoadingState(LoadingState.LOADED);
    } catch (err) {
      console.error("Error fetching fountain positions", err);
      setFountainPositionLoadingState(LoadingState.ERROR);
    }
  };
  useEffect(() => {
    void fetchFountainPositions();

    return () => {
      setFountainPositions([]);
      setSelectedFountainPositionId(null);
      setErrorMessage(null);
      setFountainPositionLoadingState(LoadingState.LOADED);
      setCreateProjectStatus(LoadingState.LOADED);
    };
  }, []);

  const onClickCreate = async () => {
    setCreateProjectStatus(LoadingState.LOADING);
    setErrorMessage(null);

    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data: position } = await axios.get(
        `/integrations/${fountainIntegration.id}/job-types/${selectedFountainPositionId}`,
      );

      if (!position) {
        setErrorMessage("Fountain position not found");
        setCreateProjectStatus(LoadingState.LOADED);
        return;
      }

      if (!position.description) {
        setErrorMessage(
          "This position is missing a job description - a job description is required to create an AI interviewer.",
        );
        setCreateProjectStatus(LoadingState.LOADED);
        return;
      }

      if (position.openings.length === 0) {
        setErrorMessage(
          "This position has no openings. At least 1 opening is required.",
        );
        setCreateProjectStatus(LoadingState.LOADED);
        return;
      }

      const firstOpening = position.openings[0];

      const newProject = await dispatch(
        createRecruitingProject({
          name: firstOpening.title || "Untitled Opening",
          jobDescription: position.description,
          jobTypeId: undefined,
          jobTypeName: position.name || "Untitled Position",
          extId: position.id,
          extProvider: "fountain",
        }),
      ).unwrap();

      const newProjectId = newProject.id;

      showNotification(dispatch, {
        id: `project-created-${new Date().getTime().toString()}`,
        primaryMessage: `Job opening successfully created`,
        type: NotificationType.SUCCESS,
      });

      setCreateProjectStatus(LoadingState.LOADED);
      navigate(buildProjectUrlPath(newProjectId));
    } catch (err) {
      console.error("error creating project", err);
      setCreateProjectStatus(LoadingState.ERROR);
      setErrorMessage("Failed to create AI interviewer. Please try again.");
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader>Create AI interviewer</ModalHeader>
      <div className="mt-4 flex flex-col space-y-4">
        <div className="relative">
          <SimpleSelect
            options={fountainPositions}
            value={selectedFountainPositionId}
            onChange={setSelectedFountainPositionId}
            label="Fountain position"
            isDisabled={isLoadingPositions}
            placeholder={
              isLoadingPositions
                ? "Loading Fountain positions..."
                : "Select a Fountain position"
            }
          />
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          An AI interviewer will be created for this Position. Add it to an AI
          Interview workflow stage to start interviewing candidates.
        </div>
        {errorMessage ? (
          <div className="mt-2 text-sm text-red-500">{errorMessage}</div>
        ) : null}
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            isDisabled={
              !selectedFountainPositionId ||
              createProjectStatus === LoadingState.LOADING
            }
            isLoading={createProjectStatus === LoadingState.LOADING}
            onClick={() => void onClickCreate()}
            variant={ButtonVariant.Primary}
          >
            {createProjectStatus === LoadingState.LOADING
              ? "Creating AI interviewer..."
              : "Create AI interviewer"}
          </Button>
        </div>
        <Button onClick={onClose} variant={ButtonVariant.Secondary}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
